import React from 'react';
import styled from 'styled-components';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';

import { ExtractedFileInfo } from '../playground/types';

export interface OcrModalProps {
  open: boolean;
  files: ExtractedFileInfo[];
  title: string;
  selectedFileIndex: number;
  copiedFileId: string | null;
  onClose: () => void;
  onCopyContent: (content: string, fileKey: string) => void;
  onPreviousFile: () => void;
  onNextFile: () => void;
}

function OcrModal({
  open,
  files,
  title,
  selectedFileIndex,
  copiedFileId,
  onClose,
  onCopyContent,
  onPreviousFile,
  onNextFile,
}: OcrModalProps) {
  if (!open) return null;

  const currentFile = files[selectedFileIndex] || null;
  const currentFileKey = currentFile?.file_id || `ocr-${selectedFileIndex}`;
  const isCurrentFileCopied = copiedFileId === currentFileKey;
  const canNavigatePrev = selectedFileIndex > 0;
  const canNavigateNext = selectedFileIndex < files.length - 1;

  return (
    <ModalOverlay onClick={onClose}>
      <OcrModalContent onClick={(e) => e.stopPropagation()}>
        <OcrModalHeader>
          <OcrModalTitleGroup>
            <OcrModalTitleText>{title || 'Texto extraído dos arquivos'}</OcrModalTitleText>
          </OcrModalTitleGroup>
          <OcrModalHeaderActions>
            {currentFile && (
              <OcrHeaderCopyButton
                type="button"
                onClick={() => onCopyContent(currentFile.file_content || '', currentFileKey)}
                disabled={!currentFile.file_content}
              >
                {isCurrentFileCopied ? <CheckCircleRoundedIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                <span>{isCurrentFileCopied ? 'Copiado' : 'Copiar'}</span>
              </OcrHeaderCopyButton>
            )}
            <CloseOcrButton onClick={onClose} aria-label="Fechar modal de texto extraído">
              <CloseIcon />
            </CloseOcrButton>
          </OcrModalHeaderActions>
        </OcrModalHeader>
        <OcrModalBody>
          {files.length > 1 && (
            <OcrFileNavigation aria-label="Navegação entre arquivos extraídos">
              <OcrNavButton
                type="button"
                onClick={onPreviousFile}
                disabled={!canNavigatePrev}
                aria-label="Arquivo anterior"
              >
                <ArrowBackIosNewIcon fontSize="inherit" />
              </OcrNavButton>
              <OcrNavLabel>
                Arquivo {selectedFileIndex + 1} de {files.length}
              </OcrNavLabel>
              <OcrNavButton
                type="button"
                onClick={onNextFile}
                disabled={!canNavigateNext}
                aria-label="Próximo arquivo"
              >
                <ArrowForwardIosIcon fontSize="inherit" />
              </OcrNavButton>
            </OcrFileNavigation>
          )}
          {currentFile ? (
            <OcrFileBlock>
              <OcrFileTitle title={currentFile.filename || undefined}>
                {currentFile.filename || 'Arquivo'}
              </OcrFileTitle>
              {currentFile.file_content ? (
                <OcrFileContent>{currentFile.file_content}</OcrFileContent>
              ) : (
                <OcrNoContentText>Sem conteúdo disponível.</OcrNoContentText>
              )}
            </OcrFileBlock>
          ) : (
            <OcrNoContentText>Sem conteúdo disponível.</OcrNoContentText>
          )}
        </OcrModalBody>
      </OcrModalContent>
    </ModalOverlay>
  );
}

export default OcrModal;

// ============ Styled Components ============

const ModalOverlay = styled.div`
  position: fixed;
  top: 0; left: 0;
  width: 100vw; height: 100vh;
  background: ${props => props.theme.modal_overlay};
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
`;

const ModalContent = styled.div`
  background: ${({ theme }) => theme.modal_background || theme.base_color};
  color: ${({ theme }) => theme.font_color};
  border: 1px solid ${({ theme }) => theme.modal_border};
  border-radius: 8px;
  padding: 20px;
  max-width: 500px;
  width: 90%;
  box-shadow: 0 4px 12px rgba(0,0,0,.1);
  position: relative;
`;

const OcrModalContent = styled(ModalContent)`
  max-width: 720px;
  width: min(92vw, 720px);
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const OcrModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 1.1rem;
  font-weight: 600;
`;

const OcrModalTitleGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const OcrModalHeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const OcrModalTitleText = styled.span`
  font-size: 1.05rem;
  font-weight: 600;
`;

const CloseOcrButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  color: inherit;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;

  &:hover {
    background: ${({ theme }) => theme.hover_overlay_light};
  }
`;

const OcrHeaderCopyButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 6px;
  border: 1px solid ${({ theme }) => theme.model_selector_option_border};
  background: ${({ theme }) => theme.model_selector_option_bg};
  color: ${({ theme }) => theme.font_color};
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.model_selector_option_hover_bg};
  }

  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
`;

const OcrModalBody = styled.div`
  overflow-y: auto;
  padding-right: 4px;
  display: flex;
  flex-direction: column;
  gap: 16px;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: ${({ theme }) => theme.model_selector_divider};
    border-radius: 4px;
  }
`;

const OcrFileNavigation = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 12px;
  margin-bottom: 12px;
`;

const OcrNavButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  border: 1px solid ${({ theme }) => theme.modal_border};
  background: transparent;
  color: ${({ theme }) => theme.font_color};
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease;
  font-size: 0.85rem;

  &:disabled {
    opacity: 0.4;
    cursor: default;
  }

  &:not(:disabled):hover {
    background: ${({ theme }) => theme.banner_highlight_bg};
    color: ${({ theme }) => theme.default_color};
  }
`;

const OcrNavLabel = styled.span`
  font-size: 0.85rem;
  font-weight: 500;
  color: ${({ theme }) => theme.font_color};
`;

const OcrFileBlock = styled.div`
  border: 1px solid ${({ theme }) => theme.modal_border};
  border-radius: 6px;
  padding: 12px;
`;

const OcrFileTitle = styled.h3`
  font-size: 0.95rem;
  margin: 0;
  font-weight: 600;
  color: ${({ theme }) => theme.font_color};
`;

const OcrFileContent = styled.pre`
  margin: 0;
  white-space: pre-wrap;
  word-break: break-word;
  font-size: 0.9rem;
  line-height: 1.4;
  font-family: inherit;
  color: ${({ theme }) => theme.font_color};
  background: transparent;
`;

const OcrNoContentText = styled.p`
  margin: 0;
  font-size: 0.9rem;
  line-height: 1.4;
  color: ${({ theme }) => theme.banner_description_color};
`;
